// /workspaces/mcn-youtube-platform/mcn-frontend/src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { UserProvider, useUser } from "./contexts/UserContext";
import Layout from './components/layout/Layout';
import getTenantConfig from './config/tenant-config';
import Homepage from './components/Homepage';
import { validateDomain } from './utils/domainValidator';
// Auth Components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ProtectedRoute, {
  AdminRoute,
  ManagerRoute,
  ContentModeratorRoute,
  FinanceRoute,
  AggregatorRoute,
  ChannelOwnerRoute,
  CreatorRoute,
  InvestorRoute,
  StudioRoute,
  SupportRoute,
  AnalyticsRoute
} from './components/auth/ProtectedRoute';

// Application Components (NEW)
import CreatorApplication from './components/CreatorApplication';
import AdminApplications from './components/AdminApplications';

// Page Components
import Unauthorized from './pages/Unauthorized';

// Existing Dashboards
import AdminDashboard from './components/dashboards/AdminDashboard';
import ChannelOwnerDashboard from './components/dashboards/ChannelOwnerDashboard';
import CreatorDashboard from './components/dashboards/CreatorDashboard';

// New Dashboards (to be created)
import SuperAdminDashboard from './components/dashboards/SuperAdminDashboard';
import ManagerDashboard from './components/dashboards/ManagerDashboard';
import FinanceDashboard from './components/dashboards/FinanceDashboard';
import ContentModerationDashboard from './components/dashboards/ContentModerationDashboard';
import AggregatorDashboard from './components/dashboards/AggregatorDashboard';
import StudioDashboard from './components/dashboards/StudioDashboard';
import InvestorDashboard from './components/dashboards/InvestorDashboard';
import SupportDashboard from './components/dashboards/SupportDashboard';
import AnalyticsDashboard from './components/dashboards/AnalyticsDashboard';

import VideoUpload from './components/VideoUpload';

// Styles
import './App.css';

// Generic Dashboard Router - Redirects to appropriate dashboard based on role
const DashboardRouter = () => {
  const { currentUser } = useUser();
  
  if (!currentUser) return <Navigate to="/login" />;
  
  const roleRoutes = {
    'SUPER_ADMIN': '/super-admin-dashboard',
    'ADMIN': '/admin-dashboard',
    'MANAGER': '/manager-dashboard',
    'FINANCE_MANAGER': '/finance-dashboard',
    'CONTENT_MANAGER': '/content-moderation',
    'AGGREGATOR': '/aggregator-dashboard',
    'CHANNEL_OWNER': '/channel-dashboard',
    'CREATOR': '/creator-dashboard',
    'CONTENT_CREATOR': '/creator-dashboard',
    'STUDIO': '/studio-dashboard',
    'INVESTOR': '/investor-dashboard',
    'PREMIUM_INVESTOR': '/investor-dashboard',
    'SUPPORT_AGENT': '/support-dashboard',
    'ANALYTICS_VIEWER': '/analytics-dashboard',
    'USER': '/creator-dashboard'
  };
  
  const targetRoute = roleRoutes[currentUser.role] || '/creator-dashboard';
  return <Navigate to={targetRoute} replace />;
};

function App() {
  const tenantConfig = getTenantConfig();
  
  // Set document title and colors based on tenant
  useEffect(() => {
    document.title = tenantConfig.name;
    // Update CSS variables for colors
    document.documentElement.style.setProperty('--primary-color', tenantConfig.primaryColor);
    document.documentElement.style.setProperty('--secondary-color', tenantConfig.secondaryColor);
  }, []);

  // Validate domain access
  useEffect(() => {
    validateDomain();
  }, []);

  return (
    <UserProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          
          {/* NEW: Public Creator Application Route */}
          <Route path="/apply" element={<CreatorApplication />} />
          
          {/* Generic Dashboard Route - Redirects based on role */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardRouter />
            </ProtectedRoute>
          } />
          
          {/* Super Admin Dashboard */}
          <Route path="/super-admin-dashboard" element={
            <Layout>
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            </Layout>
          } />
          
          {/* Admin Dashboard */}
          <Route path="/admin-dashboard" element={
            <Layout>
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            </Layout>
          } />
          
          {/* NEW: Admin Applications Review Route */}
          <Route path="/admin/applications" element={
            <Layout>
              <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN', 'MANAGER']}>
                <AdminApplications />
              </ProtectedRoute>
            </Layout>
          } />
          
          {/* Manager Dashboard */}
          <Route path="/manager-dashboard" element={
            <Layout>
              <ManagerRoute>
                <ManagerDashboard />
              </ManagerRoute>
            </Layout>
          } />
          
          {/* Finance Dashboard */}
          <Route path="/finance-dashboard" element={
            <Layout>
              <FinanceRoute>
                <FinanceDashboard />
              </FinanceRoute>
            </Layout>
          } />
          
          {/* Content Moderation Dashboard */}
          <Route path="/content-moderation" element={
            <Layout>
              <ContentModeratorRoute>
                <ContentModerationDashboard />
              </ContentModeratorRoute>
            </Layout>
          } />
          
          {/* Aggregator Dashboard */}
          <Route path="/aggregator-dashboard" element={
            <Layout>
              <AggregatorRoute>
                <AggregatorDashboard />
              </AggregatorRoute>
            </Layout>
          } />
          
          {/* Channel Owner Dashboard */}
          <Route path="/channel-dashboard" element={
            <Layout>
              <ChannelOwnerRoute>
                <ChannelOwnerDashboard />
              </ChannelOwnerRoute>
            </Layout>
          } />
          
          {/* Creator Dashboard */}
          <Route path="/creator-dashboard" element={
            <Layout>
              <CreatorRoute>
                <CreatorDashboard />
              </CreatorRoute>
            </Layout>
          } />
          
          {/* Studio Dashboard */}
          <Route path="/studio-dashboard" element={
            <Layout>
              <StudioRoute>
                <StudioDashboard />
              </StudioRoute>
            </Layout>
          } />
          
          {/* Investor Dashboard */}
          <Route path="/investor-dashboard" element={
            <Layout>
              <InvestorRoute>
                <InvestorDashboard />
              </InvestorRoute>
            </Layout>
          } />
          
          {/* Support Dashboard */}
          <Route path="/support-dashboard" element={
            <Layout>
              <SupportRoute>
                <SupportDashboard />
              </SupportRoute>
            </Layout>
          } />
          
          {/* Analytics Dashboard */}
          <Route path="/analytics-dashboard" element={
            <Layout>
              <AnalyticsRoute>
                <AnalyticsDashboard />
              </AnalyticsRoute>
            </Layout>
          } />
          
          {/* Feature Routes */}
          <Route path="/upload" element={
            <Layout>
              <ProtectedRoute roles={['CREATOR', 'CONTENT_CREATOR', 'CHANNEL_OWNER', 'ADMIN', 'SUPER_ADMIN']}>
                <VideoUpload />
              </ProtectedRoute>
            </Layout>
          } />
          
          {/* Content Management Routes */}
          <Route path="/content/pending" element={
            <Layout>
              <ContentModeratorRoute>
                <ContentModerationDashboard activeTab="pending" />
              </ContentModeratorRoute>
            </Layout>
          } />
          
          <Route path="/content/approved" element={
            <Layout>
              <ContentModeratorRoute>
                <ContentModerationDashboard activeTab="approved" />
              </ContentModeratorRoute>
            </Layout>
          } />
          
          <Route path="/content/rejected" element={
            <Layout>
              <ContentModeratorRoute>
                <ContentModerationDashboard activeTab="rejected" />
              </ContentModeratorRoute>
            </Layout>
          } />
          
          {/* Investment Routes */}
          <Route path="/investments" element={
            <Layout>
              <InvestorRoute>
                <InvestorDashboard activeTab="portfolio" />
              </InvestorRoute>
            </Layout>
          } />
          
          <Route path="/investment-marketplace" element={
            <Layout>
              <InvestorRoute>
                <InvestorDashboard activeTab="marketplace" />
              </InvestorRoute>
            </Layout>
          } />
          
          {/* Aggregator Routes */}
          <Route path="/aggregator/referrals" element={
            <Layout>
              <AggregatorRoute>
                <AggregatorDashboard activeTab="referrals" />
              </AggregatorRoute>
            </Layout>
          } />
          
          <Route path="/aggregator/commissions" element={
            <Layout>
              <AggregatorRoute>
                <AggregatorDashboard activeTab="commissions" />
              </AggregatorRoute>
            </Layout>
          } />
          
          {/* NEW: Aggregator Applications Route */}
          <Route path="/aggregator/applications" element={
            <Layout>
              <AggregatorRoute>
                <AggregatorDashboard activeTab="applications" />
              </AggregatorRoute>
            </Layout>
          } />
          
          {/* Finance Routes */}
          <Route path="/finance/payments" element={
            <Layout>
              <FinanceRoute>
                <FinanceDashboard activeTab="payments" />
              </FinanceRoute>
            </Layout>
          } />
          
          <Route path="/finance/revenue" element={
            <Layout>
              <FinanceRoute>
                <FinanceDashboard activeTab="revenue" />
              </FinanceRoute>
            </Layout>
          } />
          
          {/* Analytics Routes */}
          <Route path="/analytics/overview" element={
            <Layout>
              <AnalyticsRoute>
                <AnalyticsDashboard activeTab="overview" />
              </AnalyticsRoute>
            </Layout>
          } />
          
          <Route path="/analytics/channels" element={
            <Layout>
              <AnalyticsRoute>
                <AnalyticsDashboard activeTab="channels" />
              </AnalyticsRoute>
            </Layout>
          } />
          
         {/* Default Route - Homepage */}
<Route path="/" element={<Homepage />} />
          
          {/* 404 Route */}
          <Route path="*" element={
            <Layout>
              <div className="not-found">
                <h1>404 - Page Not Found</h1>
                <p>The page you're looking for doesn't exist.</p>
                <a href="/dashboard">Go to Dashboard</a>
              </div>
            </Layout>
          } />
        </Routes>
      </Router>
    </UserProvider>
  );
}

export default App;